import { useEffect, useState } from 'react'
import { NOTIFICATION_EVENT_TYPE, NOTIFICATION_SCHEDULE_TYPE } from '../../domain/domainCodes'
import { Mail } from 'lucide-react'
import Button from '../../components/Button'
import DatePicker from '../../components/DatePicker'
import { FormBody, InfoNote } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Modal from '../../components/Modal'
import SegmentedControl from '../../components/SegmentedControl'
import TextField from '../../components/TextField'
import { strings as S } from './strings'

const N = S.notify

/**
 * إرسال تنبيه — `_ProgramManagement_Mail_Modal.cshtml`.
 *
 * Schedules the course's start or end notification. The envelope on every course row has
 * been there since the screen was built with nothing behind it; `AddNotification` has.
 *
 * Course start/end use the canonical notification-event contract shared with the API.
 *
 * WHEN is one of three shapes and the endpoint takes them in one field:
 *
 *   minutes from now      TimeValue is the count
 *   days from now         TimeValue is the count
 *   a specific date       TimeValue is the date
 *
 * so the form shows exactly the one input the chosen shape needs, as the legacy does by
 * fading the other two out.
 */
const TYPES = [
  { id: String(NOTIFICATION_EVENT_TYPE.COURSE_START), label: N.onStart },
  { id: String(NOTIFICATION_EVENT_TYPE.COURSE_END), label: N.onEnd },
]

const WHEN = [
  { id: String(NOTIFICATION_SCHEDULE_TYPE.MINUTES_FROM_NOW), label: N.inMinutes },
  { id: String(NOTIFICATION_SCHEDULE_TYPE.DAYS_FROM_NOW), label: N.inDays },
  { id: String(NOTIFICATION_SCHEDULE_TYPE.SPECIFIC_DATE_TIME), label: N.onDate },
]

/** Today, ISO, in LOCAL time — a notification cannot be scheduled into the past. */
const today = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`
}

export default function CourseNotifyModal({ open, course, onClose, onSubmit, saving }) {
  const [type, setType] = useState(String(NOTIFICATION_EVENT_TYPE.COURSE_START))
  const [timeType, setTimeType] = useState(String(NOTIFICATION_SCHEDULE_TYPE.MINUTES_FROM_NOW))
  const [minutes, setMinutes] = useState('0')
  const [days, setDays] = useState('1')
  const [date, setDate] = useState('')
  const [delay, setDelay] = useState('0')
  const [error, setError] = useState('')

  // A second course must not inherit the first one's schedule.
  useEffect(() => {
    if (!open) return
    setType(String(NOTIFICATION_EVENT_TYPE.COURSE_START))
    setTimeType(String(NOTIFICATION_SCHEDULE_TYPE.MINUTES_FROM_NOW))
    setMinutes('0')
    setDays('1')
    setDate('')
    setDelay('0')
    setError('')
  }, [open, course?.id])

  const submit = () => {
    const timeValue = timeType === String(NOTIFICATION_SCHEDULE_TYPE.MINUTES_FROM_NOW)
      ? minutes
      : timeType === String(NOTIFICATION_SCHEDULE_TYPE.DAYS_FROM_NOW) ? days : date

    if (String(timeValue).trim() === '') {
      setError(N.whenRequired)
      return
    }

    setError('')
    onSubmit({
      Type: Number(type),
      TimeType: Number(timeType),
      TimeValue: String(timeValue),
      DelayValue: Number(delay || 0),
    })
  }

  return (
    <Modal
      open={open}
      size="sm"
      title={N.title}
      icon={Mail}
      subtitle={course?.name}
      onClose={onClose}
      footer={
        <Button disabled={saving} onClick={submit}>
          {saving ? N.sending : N.send}
        </Button>
      }
    >
      <FormBody>
        <FormRow label={N.event}>
          {() => <SegmentedControl value={type} onChange={setType} items={TYPES} ariaLabel={N.event} />}
        </FormRow>

        <FormRow label={N.when} error={error}>
          {() => <SegmentedControl value={timeType} onChange={setTimeType} items={WHEN} ariaLabel={N.when} />}
        </FormRow>

        {timeType === String(NOTIFICATION_SCHEDULE_TYPE.MINUTES_FROM_NOW) && (
          <FormRow label={N.minutes}>
            {(id) => (
              <TextField id={id} type="number" min="0" value={minutes} onChange={setMinutes} />
            )}
          </FormRow>
        )}

        {timeType === String(NOTIFICATION_SCHEDULE_TYPE.DAYS_FROM_NOW) && (
          <FormRow label={N.days}>
            {(id) => (
              <TextField id={id} type="number" min="1" max="365" value={days} onChange={setDays} />
            )}
          </FormRow>
        )}

        {timeType === String(NOTIFICATION_SCHEDULE_TYPE.SPECIFIC_DATE_TIME) && (
          <FormRow label={N.date}>
            {/* Bounded at today for the same reason the legacy checks it after the
                fact: a notification dated in the past is never sent. */}
            {(id) => <DatePicker id={id} clearable min={today()} value={date} onChange={setDate} />}
          </FormRow>
        )}

        <FormRow label={N.delay} hint={N.delayHint}>
          {(id) => (
            <TextField id={id} type="number" min="0" max="1000" value={delay} onChange={setDelay} />
          )}
        </FormRow>

        <InfoNote>
          <bdi>{N.note}</bdi>
        </InfoNote>
      </FormBody>
    </Modal>
  )
}
